import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useRouteMatch, useLocation } from 'react-router-dom';
import 'moment/locale/fr';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Footer from '../../Components/Layout/Footer';
import Routes from '../../Routing/Routes';
import Seo from '../../Routing/Seo';
import {
  CustomHitsArticle,
  CustomHitsOrganisation,
  CustomHitsTwig,
} from '../../Components/Shared/Algolia/CustomHits';
import LayoutStyles from '../../Components/Layout/Styles/LayoutStyles';
import {
  INDEX_NAMES,
  FILTERING_ATTRIBUTES,
  LEAGUE_SLUG,
  formatType,
  wordifyType,
  SEARCH_BAR_SEARCH_ATTRIBUTES,
} from '../../Services/AlgoliaHelper';
import InstantSearchResults from '../../Components/Shared/Algolia/InstantSearchResults';
import SearchBar from '../../Components/Shared/Algolia/SearchBar';
import { Hidden } from '@material-ui/core';
import { muiTheme } from '../../Themes';

const useStyles = makeStyles(theme => ({
  activityUnit: {
    paddingBottom: theme.spacing(3),
  },
  layout: {
    ...LayoutStyles.fullWidthLayout,
    paddingTop: '10px',
  },
  subHeroLayout: {
    // Make the picture goes under the navbar
    ...LayoutStyles.navbarPaddingTop,

    width: '100%',
    backgroundColor: theme.reprise.primary.main,
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    backgroundPositionY: '60%',
    padding: '3rem 0',
  },
  headingContainer: {
    width: '100%',
    padding: '3rem 0 3rem',
  },
  searchBar: {
    marginTop: 0,
  },
  results: {
    minHeight: '60vh',
  },
}));

function SearchResult() {
  const match = useRouteMatch();
  const location = useLocation();
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    params: { type_slug: typeSlug },
  } = match;
  const type = formatType(typeSlug);
  const typeName = wordifyType(typeSlug);
  const queryParam = new URLSearchParams(location.search);
  const urlParams = {
    city: queryParam.get('city') || null,
    address: queryParam.get('address') || '',
    latitude: queryParam.get('lat') || null,
    longitude: queryParam.get('long') || null,
    bbox: queryParam.get('bbox') || null,
    pathology: queryParam.get(FILTERING_ATTRIBUTES.ORGANISATION_PATHOLOGY_TYPE_ATTRIBUTE) || '',
  };
  const position = urlParams.latitude != null && urlParams.longitude != null;
  const bbox = urlParams.bbox?.split(';')?.map(val => parseFloat(val, 10)) || null;
  const dateNow = Math.floor(Date.now() / 1000);

  const typeAttributes = {
    organisations: {
      indexName: INDEX_NAMES.ORGANISATION,
      title: 'APP.SEARCH_RESULT_PAGE.ORGANISATIONS',
      aroundCityPrefix: 'APP.SEARCH_RESULT_PAGE.CLOSE_TO',
      showCityInfo: true,
      showKeywordInfo: false,
      configureSettings: {
        hitsPerPage: 15,
        query: urlParams.serviceSlug,
        getRankingInfo: true,
        filters: `${FILTERING_ATTRIBUTES.LEAGUE_ATTRIBUTE}:${LEAGUE_SLUG.Reprise}`,
      },
      HitsDisplay: CustomHitsOrganisation,
      hasMap: true,
      refinementAttributes: [
        {
          label: 'Types de structure',
          attribute: FILTERING_ATTRIBUTES.ORGANISATION_TYPE_ATTRIBUTE,
        },
      ],
      hideKeywordField: true,
      hideCityField: false,
    },
    twigs: {
      indexName: INDEX_NAMES.TWIG,
      title: 'REPRISE.SEARCH_RESULT_PAGE.TWIGS',
      configureSettings: {
        hitsPerPage: 12,
        query: urlParams.serviceSlug,
        filters: `${FILTERING_ATTRIBUTES.LEAGUE_ATTRIBUTE}:${LEAGUE_SLUG.Reprise} AND ${FILTERING_ATTRIBUTES.TWIG_DATE_ATTRIBUTE} > ${dateNow}`,
      },
      HitsDisplay: CustomHitsTwig,
      hasMap: true,
      // refinementAttributes: [],
      hideKeywordField: true,
      hideCityField: false,
      // cardSettings: {
      //   hideTaskInfo: true,
      //    rowItemSM: 3,
      // },
    },
    articles: {
      indexName: INDEX_NAMES.ARTICLE,
      title: 'APP.SEARCH_RESULT_PAGE.ARTICLES',
      configureSettings: {
        hitsPerPage: 10,
        query: urlParams.serviceSlug,
        filters: `${FILTERING_ATTRIBUTES.LEAGUE_SLUG_ATTRIBUTE}:${LEAGUE_SLUG.Reprise}`,
      },
      HitsDisplay: CustomHitsArticle,
      hasMap: false,
      showKeywordInfo: true,
      // refinementAttributes: [],
      hideKeywordField: false,
      hideCityField: true,
    },
  };

  return (
    <Grid>
      <Helmet>
        {urlParams.city &&
          ['organisations', 'twigs'].includes(type) &&
          Seo.title(
            `${typeName} à ${urlParams.city}${t(`${Routes.currentNamespace()}.SEO.SUFFIX`)}`,
          )}
        {(!urlParams.city || !['organisations', 'twigs'].includes(type)) &&
          Seo.title(`Résultats ${typeName}${t(`${Routes.currentNamespace()}.SEO.SUFFIX`)}`)}
        {Seo.canonical(
          Routes.currentLeague().id,
          `${location.pathname}${location.search.replaceAll(' ', '%20')}`,
        )}
      </Helmet>

      <Hidden only={['xs']}>
        <Grid container className={classes.container}>
          <Grid item className={classes.subHeroLayout}>
            <Grid item className={classes.layout} style={{ paddingTop: 0 }}>
              <SearchBar
                className={classes.searchBar}
                service={urlParams.serviceSlug}
                address={urlParams.address}
                city={urlParams.city}
                latitude={urlParams.latitude}
                longitude={urlParams.longitude}
                bbox={bbox}
                type={type}
                goBackgrounColor={muiTheme.reprise.secondary.main}
                // geographicLimits={GEOGRAPHIC_LIMITS.SLA}
                searchAttributes={SEARCH_BAR_SEARCH_ATTRIBUTES.Reprise}
                defaultType="structures"
                hideKeywordField={typeAttributes[type].hideKeywordField}
                hideCityField={typeAttributes[type].hideCityField}
                AutoCompleteWidth={7}
              />
            </Grid>
          </Grid>
        </Grid>
      </Hidden>

      <Grid style={{ minHeight: '80vh' }}>
        <InstantSearchResults
          request={typeAttributes[type]}
          position={position ? { lat: urlParams.latitude, lng: urlParams.longitude } : null}
          bbox={bbox}
          typeAttributes={typeAttributes}
          tabs={[
            {
              label: t('APP.SEARCH_RESULT_PAGE.TAB.ORGANISATIONS'),
              value: 'organisations',
            },
            { label: t('APP.SEARCH_RESULT_PAGE.TAB.TWIGS'), value: 'twigs' },
            { label: t('APP.SEARCH_RESULT_PAGE.TAB.ARTICLES'), value: 'articles' },
          ]}
        />
      </Grid>
      <Footer />
    </Grid>
  );
}

export default SearchResult;
